import { LikelihoodColorLevel, RiskLevel } from 'src/app/models/risk-assessment.model';

// Accepted by resolveExposureBandLabel — covers both the grid's LikelihoodColorLevel (uppercase
// QuantitativeName) and the popup's internal level shape (lowercase quantitativeName).
// Named distinctly from _helpers/risk-color.helper.ts's getLikelihoodBandLabel (develop) —
// different signature/return shape and opposite blank-name fallback semantics; avoids a
// same-name conflict at the staging→develop backmerge (L2).
type BandLevelInput = {
    QuantitativeName?: string;
    quantitativeName?: string;
    minQuantThreshold?: number | null;
    maxQuantThreshold?: number | null;
} | undefined;

/**
 * Returns the Exposure Band label and % OEL range for one configured likelihood level.
 * Criteria 9, 10:
 *  - bandName = QuantitativeName only; no silent fallback to Name (H3).
 *  - bandRange rendered only when min < max strict ascending interval (H2).
 */
export function resolveExposureBandLabel(
    level: BandLevelInput,
    naText: string,
): { bandName: string; bandRange: string } {
    const rawName = ((level as { QuantitativeName?: string })?.QuantitativeName
        ?? (level as { quantitativeName?: string })?.quantitativeName
        ?? '').trim();
    const bandName = rawName || naText;
    const min = Number(level?.minQuantThreshold);
    const max = Number(level?.maxQuantThreshold);
    const bandRange = Number.isFinite(min) && Number.isFinite(max) && min < max
        ? `${min}–${max}`
        : '';
    return { bandName, bandRange };
}

/**
 * Parses a jurisdiction/tenant likelihood-levels JSON blob into typed, key-sorted
 * `LikelihoodColorLevel` entries. Normalizes the array-vs-object shape, drops entries
 * with non-numeric keys, and sorts ascending by key.
 *
 * Shared by `RiskRegisterComponent` and the report popups so this parsing logic lives in
 * one place (DRY). Pass `defaultColor` to control the fallback used when a level omits its
 * colour (e.g. the grey used on the Risk Register cards).
 */
export function parseLikelihoodColorLevels(
    levelsJson: string | undefined,
    defaultColor = '',
): LikelihoodColorLevel[] {
    if (!levelsJson) {
        return [];
    }

    try {
        const parsedLevels = JSON.parse(levelsJson);
        const entries: [string, unknown][] = Array.isArray(parsedLevels)
            ? parsedLevels.map((value, index) => [String(index + 1), value])
            : Object.entries(parsedLevels ?? {});

        return entries
            .map(([key, value]) => {
                const numericKey = Number(key);
                if (
                    !Number.isFinite(numericKey) ||
                    !value ||
                    typeof value !== 'object' ||
                    Array.isArray(value)
                ) {
                    return null;
                }

                const level = value as {
                    Color?: string;
                    Name?: string;
                    QuantitativeName?: string;
                    Description?: string;
                    MinSamples?: number;
                    ReassessmentFrequencyMonths?: number;
                    QuantThreshold_OEL?: [number, number];
                    QualThresholds?: [number, number];
                    minPercent?: number;
                    maxPercent?: number;
                };
                const thresholds = Array.isArray(level.QuantThreshold_OEL)
                    ? level.QuantThreshold_OEL
                    : [level.minPercent, level.maxPercent];
                const qualThresholds = Array.isArray(level.QualThresholds)
                    ? level.QualThresholds
                    : [undefined, undefined];
                return {
                    key: numericKey,
                    Color: level.Color ?? defaultColor,
                    Name: level.Name,
                    QuantitativeName: level.QuantitativeName,
                    Description: typeof level.Description === 'string' ? level.Description : undefined,
                    MinSamples: level.MinSamples != null ? Number(level.MinSamples) : undefined,
                    ReassessmentFrequencyMonths: level.ReassessmentFrequencyMonths != null ? Number(level.ReassessmentFrequencyMonths) : undefined,
                    minQuantThreshold: Number(thresholds[0]),
                    maxQuantThreshold: Number(thresholds[1]),
                    minQualThreshold: Number(qualThresholds[0]),
                    maxQualThreshold: Number(qualThresholds[1]),
                } as LikelihoodColorLevel;
            })
            .filter((level): level is LikelihoodColorLevel => !!level)
            .sort((a, b) => a.key - b.key);
    } catch {
        return [];
    }
}

/**
 * Parses a risk-levels JSON blob into typed `RiskLevel` entries that have valid numeric
 * thresholds, sorted ascending by their lower threshold. Shared by `RiskRegisterComponent`
 * and the report popups (DRY).
 */
export function parseRiskLevels(levelsJson: string | undefined): RiskLevel[] {
    if (!levelsJson) {
        return [];
    }

    try {
        const parsedLevels = JSON.parse(levelsJson);
        const rawLevels = (Array.isArray(parsedLevels)
            ? parsedLevels
            : Object.values(parsedLevels ?? {})) as RiskLevel[];

        return rawLevels
            .filter((level) => {
                const thresholds = level?.Thresholds;
                return (
                    Array.isArray(thresholds) &&
                    Number.isFinite(Number(thresholds[0])) &&
                    Number.isFinite(Number(thresholds[1]))
                );
            })
            .sort((a, b) => Number(a.Thresholds[0]) - Number(b.Thresholds[0]));
    } catch {
        return [];
    }
}
